import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAlert } from "../../contexts/AlertContext";
import {
  Box,
  Typography,
  Divider,
  Chip,
  Stack,
  TextField,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table as MuiTable,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BuildIcon from "@mui/icons-material/Build";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import { productsApi, ProductApiResponse, ProductDocumentInfo, ProductPzWzAllocationsResponse } from "../../services/productsApi";
import { bundlesApi, BundleDefinition, BundleInstance } from "../../services/bundlesApi";
import { useTranslation } from "react-i18next";
import CustomBox from "../CustomBox/CustomBox";
import Table, { Column } from "../Table/Table";
import { LabelChip } from "../Labels";
import CategorySelector from "../CategorySelector/CategorySelector";
import { useWarehouse } from "../../contexts/WarehouseContext";
import { ProductLocationPanel } from "../WarehouseMap";
import { BarcodeManager } from "../BarcodeManager";
import { PzWzAllocationsPanel } from "../PzWzAllocations/PzWzAllocationsPanel";
import {
  getTextFieldStyles as getFormTextFieldStyles,
  getAutocompleteListboxStyles,
} from "../../styles/formStyles";

const MS_IN_DAY = 24 * 60 * 60 * 1000;

function daysToDateInput(days?: number | null): string {
  if (!days || days <= 0) return "";
  const dt = new Date();
  dt.setHours(0, 0, 0, 0);
  dt.setDate(dt.getDate() + days);
  return dt.toISOString().split("T")[0];
}

function dateInputToDays(value: string): number | null {
  if (!value) return null;
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsedDate.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((parsedDate.getTime() - today.getTime()) / MS_IN_DAY);
  return diffDays > 0 ? diffDays : null;
}

const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#474747ff" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
    "&.Mui-disabled": {
      "& fieldset": { borderColor: "rgba(255, 255, 255, 0.23)" },
      "& .MuiInputBase-input": { color: "rgba(255, 255, 255, 0.5)" },
      "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "rgba(255, 255, 255, 0.5)" },
    },
  },
  "& .MuiInputLabel-root": { color: "text.secondary" },
  "& .MuiInputLabel-root.Mui-focused": { color: "text.secondary" },
  "& .MuiInputLabel-root.Mui-disabled": { color: "rgba(255, 255, 255, 0.5)" },
  "& .MuiInputBase-input": { color: "text.primary" },
  "& .hide-number-spinners": {
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "&[type=number]": {
      MozAppearance: "textfield",
    },
  },
};

interface FieldProps {
  key: string;
  label: string;
  placeholder?: string;
  type?: string;
  endAdornment?: React.ReactNode;
  disabled?: boolean;
  /** When set, render MUI Select instead of TextField */
  component?: "text" | "select";
  options?: { value: string; label: string }[];
  getValue: (product: ProductApiResponse) => any;
  setValue?: (product: Partial<ProductApiResponse>, value: any) => Partial<ProductApiResponse>;
}

const LabeledTextField: React.FC<{
  label: string;
  value: any;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  endAdornment?: React.ReactNode;
  disabled?: boolean;
}> = ({ label, value, onChange, placeholder, type, endAdornment, disabled }) => (
  <TextField
    fullWidth
    label={label}
    value={value ?? ""}
    onChange={onChange}
    variant="outlined"
    size="small"
    placeholder={placeholder}
    type={type}
    disabled={disabled}
    sx={textFieldStyles}
    autoComplete="off"
    InputProps={{
      endAdornment,
      inputProps: {
        inputMode: type === "number" ? "numeric" : undefined,
        className: type === "number" ? "hide-number-spinners" : "",
      },
    }}
    InputLabelProps={type === "date" ? { shrink: true } : undefined}
  />
);

export const ProductDetails: React.FC = () => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { selectedWarehouse } = useWarehouse();
  const [product, setProduct] = useState<ProductApiResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [relatedDocuments, setRelatedDocuments] = useState<ProductDocumentInfo[]>([]);
  const [totalDocsCount, setTotalDocsCount] = useState(0);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsPage, setDocsPage] = useState(0);
  const [docsRowsPerPage, setDocsRowsPerPage] = useState(10);
  const [docsSearch, setDocsSearch] = useState("");
  const [allocations, setAllocations] = useState<ProductPzWzAllocationsResponse | null>(null);
  const [allocationsLoading, setAllocationsLoading] = useState(false);
  const productIdRef = useRef<string | undefined>(id);

  // Bundle state
  const [bundleDefinitions, setBundleDefinitions] = useState<BundleDefinition[]>([]);
  const [bundleInstances, setBundleInstances] = useState<BundleInstance[]>([]);
  const [bundleAssembleOpen, setBundleAssembleOpen] = useState(false);
  const [bundleAssembleDef, setBundleAssembleDef] = useState<BundleDefinition | null>(null);
  const [bundleAssembleQty, setBundleAssembleQty] = useState(1);
  const [bundleAssembleSaving, setBundleAssembleSaving] = useState(false);

  const DOC_STATUS_LABELS: Record<string, string> = {
    oczekujaca: "Oczekująca",
    zweryfikowano: "Zweryfikowano",
    nowy: "Nowy",
    zrealizowany: "Zrealizowany",
    w_realizacji: "W trakcie realizacji",
    wyslana: "Wysłana",
    dostarczona: "Dostarczona",
    anulowany: "Anulowany",
  };

  const relatedDocsColumns: Column<ProductDocumentInfo>[] = [
    {
      id: "document_number",
      label: t("documents.columns.documentNumber") || "Numer",
      flex: 1,
      format: (_, row) => (
        <span style={{ color: "#90caf9" }}>{row.document_number}</span>
      ),
    },
    {
      id: "document_type_display",
      label: t("documents.columns.documentType") || "Typ",
      flex: 1,
      format: (_, row) => (
        <Chip
          label={row.document_type_display}
          size="small"
          variant="outlined"
          sx={{
            borderColor: "#474747ff",
            color: "white",
            "& .MuiChip-label": { color: "white" },
          }}
        />
      ),
    },
    {
      id: "document_status",
      label: t("documents.columns.status") || "Status",
      flex: 1,
      format: (_, row) => (
        <Chip
          label={DOC_STATUS_LABELS[row.document_status] || row.document_status}
          size="small"
          color={
            ["zrealizowany", "zweryfikowano", "dostarczona"].includes(row.document_status)
              ? "success"
              : row.document_status === "anulowany"
                ? "error"
                : ["w_realizacji", "wyslana"].includes(row.document_status)
                  ? "info"
                  : "warning"
          }
          variant="outlined"
          sx={{ color: "white", "& .MuiChip-label": { color: "white" } }}
        />
      ),
    },
    {
      id: "document_date",
      label: t("documents.columns.date") || "Data",
      flex: 1,
    },
    {
      id: "quantity_in_document",
      label: t("documents.columns.quantity") || "Ilość",
      align: "right",
      flex: 1,
    },
    {
      id: "storage_location_code",
      label: t("documents.columns.storageLocation") || "Lokalizacja",
      flex: 1,
      format: (val) => val || "-",
    },
  ];

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) {
        setError("Brak ID produktu");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const productId = parseInt(id, 10);
        if (isNaN(productId)) {
          throw new Error("Nieprawidłowe ID produktu");
        }
        const fetchedProduct = await productsApi.getProduct(productId);
        setProduct(fetchedProduct);
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.detail ||
          err.message ||
          "Błąd podczas pobierania produktu";
        setError(errorMessage);
        showAlert("error", errorMessage, 5000);
        console.error("Błąd podczas pobierania produktu:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [id, showAlert]);

  useEffect(() => {
    if (!id) return;
    if (productIdRef.current !== id) {
      productIdRef.current = id;
      setDocsPage(0);
      setDocsSearch("");
      return;
    }
    const productId = parseInt(id, 10);
    if (isNaN(productId)) return;
    let cancelled = false;
    const fetchDocuments = async () => {
      try {
        setDocsLoading(true);
        const response = await productsApi.getProductDocuments(productId, {
          page: docsPage,
          page_size: docsRowsPerPage,
          search: docsSearch || undefined,
        });
        if (!cancelled) {
          setRelatedDocuments(response.results);
          setTotalDocsCount(response.count);
        }
      } catch {
        if (!cancelled) {
          setRelatedDocuments([]);
          setTotalDocsCount(0);
        }
      } finally {
        if (!cancelled) setDocsLoading(false);
      }
    };
    fetchDocuments();
    return () => {
      cancelled = true;
    };
  }, [id, docsPage, docsRowsPerPage, docsSearch]);

  useEffect(() => {
    if (!id) return;
    const productId = parseInt(id, 10);
    if (isNaN(productId)) return;
    let cancelled = false;
    const fetchAllocations = async () => {
      try {
        setAllocationsLoading(true);
        const data = await productsApi.getPzWzAllocations(
          productId,
          selectedWarehouse?.id
        );
        if (!cancelled) setAllocations(data);
      } catch {
        if (!cancelled) setAllocations(null);
      } finally {
        if (!cancelled) setAllocationsLoading(false);
      }
    };
    fetchAllocations();
    return () => {
      cancelled = true;
    };
  }, [id, selectedWarehouse?.id]);

  // Load bundle data for this product
  useEffect(() => {
    if (!id) return;
    const productId = parseInt(id, 10);
    if (isNaN(productId)) return;

    const loadBundleData = async () => {
      try {
        const [defs, insts] = await Promise.all([
          bundlesApi.getDefinitions({ product_id: productId }),
          bundlesApi.getInstances({ product_id: productId, active_only: true }),
        ]);
        setBundleDefinitions(defs);
        setBundleInstances(insts);
      } catch {
        /* non-critical */
      }
    };
    loadBundleData();
  }, [id]);

  const handleBundleAssemble = async () => {
    if (!bundleAssembleDef || bundleAssembleQty < 1) return;
    setBundleAssembleSaving(true);
    try {
      const result = await bundlesApi.assemble({
        bundle_definition_id: bundleAssembleDef.id,
        quantity: bundleAssembleQty,
      });
      showAlert("success", `Pakiet zmontowany. Dokument: ${result.document_number}`);
      setBundleAssembleOpen(false);
      setBundleAssembleDef(null);
      setBundleAssembleQty(1);
      // Reload product and instances
      if (id) {
        const productId = parseInt(id, 10);
        const [updatedProduct, insts] = await Promise.all([
          productsApi.getProduct(productId),
          bundlesApi.getInstances({ product_id: productId, active_only: true }),
        ]);
        setProduct(updatedProduct);
        setBundleInstances(insts);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Nie udało się zmontować pakietu";
      showAlert("error", msg);
    } finally {
      setBundleAssembleSaving(false);
    }
  };

  const handleDisassembleInstance = async (instance: BundleInstance) => {
    if (!window.confirm(`Czy na pewno chcesz rozmontować pakiet "${instance.definition_name}" (${instance.quantity} szt.)?`)) return;
    try {
      const result = await bundlesApi.disassemble({ bundle_instance_id: instance.id });
      showAlert("success", `Pakiet rozmontowany. Dokument: ${result.document_number}`);
      if (id) {
        const productId = parseInt(id, 10);
        const [updatedProduct, insts] = await Promise.all([
          productsApi.getProduct(productId),
          bundlesApi.getInstances({ product_id: productId, active_only: true }),
        ]);
        setProduct(updatedProduct);
        setBundleInstances(insts);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Nie udało się rozmontować pakietu";
      showAlert("error", msg);
    }
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!product) return;

    let value: any = e.target.value;

    // Parse numeric fields
    if (["quantity", "price"].includes(field)) {
      if (field === "price") {
        value = value.toString(); // API expects price as string
      } else {
        value = parseFloat(value) || 0;
      }
    } else if (field === "expiry_warning_days") {
      if (value === "" || value === null) {
        value = null;
      } else {
        const parsed = parseInt(value, 10);
        value = Number.isNaN(parsed) ? null : parsed;
      }
    } else if (field === "shelf_life_days") {
      value = dateInputToDays(value);
    }

    setProduct((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleSelectChange = (field: string) => (e: SelectChangeEvent<string>) => {
    if (!product) return;
    setProduct((prev) => (prev ? { ...prev, [field]: e.target.value } : null));
  };

  const handleSave = async () => {
    if (!product) return;

    try {
      setIsSaving(true);
      const updatedProduct = await productsApi.updateProduct(product.id, {
        name: product.name,
        description: product.description,
        category: product.category,
        product_code: product.product_code,
        price: product.price,
        quantity: product.quantity,
        picking_stock_rotation: product.picking_stock_rotation ?? "fifo",
        shelf_life_days: product.shelf_life_days ?? null,
        expiry_warning_days: product.expiry_warning_days ?? null,
        block_expired_sales:
          typeof product.block_expired_sales === "string"
            ? (product.block_expired_sales as unknown as string) === "true"
            : Boolean(product.block_expired_sales),
      });
      setProduct(updatedProduct);
      showAlert("success", t("products.messages.updated") || "Produkt zaktualizowany");
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        "Błąd podczas zapisywania produktu";
      showAlert("error", errorMessage, 5000);
      console.error("Błąd podczas zapisywania produktu:", err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" mb={2}>
          <IconButton
            onClick={() => navigate("/products")}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography color="white" variant="h6" fontWeight={500}>
            {t("products.details.details")}
          </Typography>
        </Stack>
        <CustomBox>
          <Typography color="white" variant="h6">
            {error || "Produkt nie został znaleziony"}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/products")}
            sx={{ mt: 2 }}
          >
            {t("common.back") || "Powrót"}
          </Button>
        </CustomBox>
      </Box>
    );
  }

  const sections: { title: string; fields: FieldProps[] }[] = [
    {
      title: t("products.details.basicInfo") || "Podstawowe informacje",
      fields: [
        {
          key: "product_code",
          label: t("products.form.productCode") || "Kod produktu",
          getValue: (p) => p.product_code || "",
        },
      ],
    },
    {
      title: t("products.details.stockStatus") || "Stan magazynowy",
      fields: [
        {
          key: "quantity",
          label: t("products.form.availableQuantity") || "Dostępna ilość",
          type: "number",
          disabled: true,
          getValue: (p) => p.quantity,
        },
        ...(product?.in_bundles_quantity ? [{
          key: "in_bundles_quantity" as const,
          label: "W pakietach",
          type: "number" as const,
          disabled: true,
          getValue: (p: any) => p.in_bundles_quantity || 0,
        }] : []),
        {
          key: "updated_at",
          label: t("products.form.lastUpdated") || "Ostatnia aktualizacja",
          getValue: (p) => p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "",
          disabled: true,
        },
      ],
    },
    {
      title: t("products.details.pickingSection") || "Zbieranie z magazynu",
      fields: [
        {
          key: "picking_stock_rotation",
          label: t("products.form.pickingStockRotation") || "Rotacja przy zbieraniu",
          component: "select",
          options: [
            { value: "fifo", label: t("products.form.pickingFifo") || "FIFO" },
            { value: "lifo", label: t("products.form.pickingLifo") || "LIFO" },
          ],
          getValue: (p) => p.picking_stock_rotation ?? "fifo",
        },
      ],
    },
    {
      title: t("products.details.expirySection") || "Termin ważności",
      fields: [
        {
          key: "shelf_life_days",
          label: t("products.form.initialExpiryDate") || "Data ważności",
          type: "date",
          getValue: (p) =>
            p.shelf_life_days === null || p.shelf_life_days === undefined
              ? ""
              : daysToDateInput(p.shelf_life_days),
        },
        {
          key: "expiry_warning_days",
          label:
            t("products.form.expiryWarningDays") ||
            "Próg ostrzeżenia (dni przed końcem)",
          type: "number",
          placeholder:
            t("products.form.expiryWarningDaysPlaceholder") || "np. 7",
          getValue: (p) =>
            p.expiry_warning_days === null || p.expiry_warning_days === undefined
              ? ""
              : p.expiry_warning_days,
        },
        {
          key: "block_expired_sales",
          label:
            t("products.form.blockExpiredSales") ||
            "Blokada wydania po terminie",
          component: "select",
          options: [
            {
              value: "false",
              label: t("common.no") || "Nie",
            },
            {
              value: "true",
              label: t("common.yes") || "Tak",
            },
          ],
          getValue: (p) => (p.block_expired_sales ? "true" : "false"),
        },
      ],
    },
    {
      title: t("products.details.pricesAndMargins") || "Ceny",
      fields: [
        {
          key: "price",
          label: t("products.form.price") || "Cena",
          type: "number",
          getValue: (p) => parseFloat(p.price) || 0,
          endAdornment: <Typography>zł</Typography>,
        },
      ],
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" mb={2}>
        <IconButton
          onClick={() => navigate("/products")}
          sx={{
            color: "white",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography color="white" variant="h6" fontWeight={500}>
          {t("products.details.details") || "Szczegóły produktu"}
        </Typography>
      </Stack>

      <CustomBox>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" gutterBottom color="white">
              {product.name}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {Array.isArray(product.category) && product.category.length > 0
                ? product.category.map((cat, index) => (
                  <Chip
                    key={index}
                    label={cat}
                    color="primary"
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: "#474747ff",
                      color: "white",
                      "& .MuiChip-label": { color: "white" },
                    }}
                  />
                ))
                : product.category && (
                  <Chip
                    label={product.category}
                    color="primary"
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: "#474747ff",
                      color: "white",
                      "& .MuiChip-label": { color: "white" },
                    }}
                  />
                )}
            </Stack>
          </Box>

          <Divider />

          {sections.map((section) => (
            <Stack key={section.title} spacing={2}>
              <Typography variant="h6" color="textSecondary">
                {section.title}
              </Typography>
              <Stack spacing={1} direction={{ xs: "column", md: "row" }}>
                {section.fields.map((field) => (
                  <Stack flex={1} key={field.key}>
                    {field.component === "select" && field.options ? (
                      <FormControl fullWidth size="small" sx={textFieldStyles}>
                        <InputLabel id={`${field.key}-label`}>{field.label}</InputLabel>
                        <Select
                          labelId={`${field.key}-label`}
                          label={field.label}
                          value={field.getValue(product) ?? "fifo"}
                          onChange={handleSelectChange(field.key)}
                        >
                          {field.options.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <LabeledTextField
                        label={field.label}
                        value={field.getValue(product)}
                        onChange={handleChange(field.key)}
                        type={field.type}
                        placeholder={field.placeholder}
                        endAdornment={field.endAdornment}
                        disabled={field.disabled}
                      />
                    )}
                  </Stack>
                ))}
              </Stack>
            </Stack>
          ))}

          <Divider />
          <BarcodeManager productId={product.id} productCode={product.product_code || ""} />

          <Stack spacing={2}>
            <Typography variant="h6" color="textSecondary">
              {t("products.columns.category") || "Kategoria"}
            </Typography>
            <CategorySelector
              value={Array.isArray(product.category) ? product.category : []}
              onChange={(categories) =>
                setProduct((prev) => (prev ? { ...prev, category: categories } : null))
              }
              label={t("products.columns.category") || "Kategoria"}
            />
          </Stack>

          {product.suppliers && product.suppliers.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  {t("products.form.suppliers") || "Dostawcy"}
                </Typography>
                <Stack spacing={2}>
                  {product.suppliers.map((supplier) => (
                    <Box
                      key={supplier.id}
                      sx={{
                        p: 2,
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: "8px",
                        backgroundColor: "rgba(255, 255, 255, 0.02)",
                      }}
                    >
                      <Typography variant="body1" color="white" fontWeight={500}>
                        {supplier.name}
                      </Typography>
                      <Stack direction="row" spacing={1} mt={0.5}>
                        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                          {supplier.email}
                        </Typography>
                        {supplier.phone && (
                          <>
                            <Typography variant="body2" color="rgba(255, 255, 255, 0.5)">
                              •
                            </Typography>
                            <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                              {supplier.phone}
                            </Typography>
                          </>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {product.labels && product.labels.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  {t("products.form.labels") || "Etykiety"}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {product.labels.map((label) => (
                    <LabelChip key={label.id} label={label} size="small" />
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {/* Bundle section */}
          <Divider />
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" color="textSecondary">
                {t("bundles.title") || "Pakiety"}
              </Typography>
              <Button
                size="small"
                endIcon={<OpenInNewIcon />}
                onClick={() => navigate("/bundles")}
                sx={{ color: "primary.main" }}
              >
                {t("bundles.viewAll") || "Zobacz wszystkie pakiety"}
              </Button>
            </Stack>

            {product?.is_bundle && product?.bundle_components && (
              <>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  {t("bundles.componentsLabel") || "Komponenty pakietu"}
                </Typography>
                <MuiTable size="small" sx={{ mb: 2, "& .MuiTableCell-root": { borderColor: "rgba(255,255,255,0.12)", color: "white" } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Produkt</TableCell>
                      <TableCell>Kod</TableCell>
                      <TableCell align="right">Ilość na pakiet</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {product.bundle_components.map((comp: any) => (
                      <TableRow key={comp.product_id}>
                        <TableCell>{comp.product_name}</TableCell>
                        <TableCell>{comp.product_code}</TableCell>
                        <TableCell align="right">{comp.quantity_per_bundle} szt.</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </MuiTable>

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<BuildIcon />}
                    onClick={() => setBundleAssembleOpen(true)}
                    sx={{ borderColor: "#474747ff", color: "white", "&:hover": { borderColor: "primary.main" } }}
                  >
                    {t("bundles.assemble") || "Montuj pakiet"}
                  </Button>
                </Stack>
              </>
            )}

            {!product?.is_bundle && (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<BuildIcon />}
                  onClick={() => navigate("/bundles")}
                  sx={{ borderColor: "#474747ff", color: "white", "&:hover": { borderColor: "primary.main" } }}
                >
                  {t("bundles.createDefinition") || "Utwórz definicję pakietu"}
                </Button>
              </Stack>
            )}

            {/* Used in bundles section */}
            {product?.used_in_bundles && product.used_in_bundles.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  {t("bundles.usedInBundles") || "Użyty w pakietach"}
                </Typography>
                <MuiTable size="small" sx={{ mb: 2, "& .MuiTableCell-root": { borderColor: "rgba(255,255,255,0.12)", color: "white" } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("bundles.columns.product") || "Pakiet"}</TableCell>
                      <TableCell>{t("bundles.columns.code") || "Kod"}</TableCell>
                      <TableCell align="right">{t("bundles.quantityPerBundle") || "Ilość na pakiet"}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {product.used_in_bundles.map((b: { bundle_id: number; bundle_product_id: number; bundle_name: string; bundle_code: string; quantity_per_bundle: number }) => (
                      <TableRow
                        key={b.bundle_id}
                        hover
                        sx={{ cursor: "pointer", "&:hover": { bgcolor: "rgba(255,255,255,0.04)" } }}
                        onClick={() => navigate(`/products/${b.bundle_product_id}`)}
                      >
                        <TableCell>{b.bundle_name}</TableCell>
                        <TableCell>{b.bundle_code}</TableCell>
                        <TableCell align="right">{b.quantity_per_bundle} szt.</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </MuiTable>
              </Box>
            )}

            {/* Active bundle instances for this product */}
            {bundleInstances.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  {t("bundles.activeInstances") || "Aktywne instancje"}
                </Typography>
                <MuiTable size="small" sx={{ "& .MuiTableCell-root": { borderColor: "rgba(255,255,255,0.12)", color: "white" } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("bundles.columns.bundle") || "Pakiet"}</TableCell>
                      <TableCell align="right">{t("bundles.columns.quantity") || "Ilość"}</TableCell>
                      <TableCell>{t("bundles.columns.document") || "Dokument"}</TableCell>
                      <TableCell>{t("common.actions") || "Akcje"}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bundleInstances.map((inst) => (
                      <TableRow key={inst.id}>
                        <TableCell>{inst.definition_name}</TableCell>
                        <TableCell align="right">{inst.quantity} szt.</TableCell>
                        <TableCell>{inst.document_number || "—"}</TableCell>
                        <TableCell>
                          {!inst.is_disassembled && (
                            <Button
                              size="small"
                              startIcon={<CallSplitIcon />}
                              onClick={() => handleDisassembleInstance(inst)}
                              sx={{ color: "warning.main" }}
                            >
                              {t("bundles.disassemble") || "Rozmontuj"}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </MuiTable>
              </Box>
            )}
          </Box>

          <Divider />
          <Box>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {t("products.details.locations") || "Lokalizacje w magazynie"}
            </Typography>
            {selectedWarehouse?.id ? (
              <Box
                sx={{
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "rgba(255, 255, 255, 0.02)",
                }}
              >
                <ProductLocationPanel
                  productId={product.id}
                  productName={product.name}
                  productCode={product.product_code}
                  warehouseId={selectedWarehouse.id}
                  warehouseName={selectedWarehouse.name}
                  onShowOnMap={(locationIds) => {
                    const params = new URLSearchParams({
                      warehouse: String(selectedWarehouse.id),
                      highlight: locationIds.join(","),
                      productId: String(product.id),
                      productName: product.name,
                      productCode: product.product_code || "",
                    });
                    navigate(`/warehouse-map?${params.toString()}`);
                  }}
                />
              </Box>
            ) : (
              <Alert severity="info" sx={{ bgcolor: "rgba(255, 255, 255, 0.05)" }}>
                {t("products.details.selectWarehouseForLocations") ||
                  "Wybierz magazyn w górnym pasku, aby zobaczyć lokalizacje produktu (regał, półka, slot) i opcję „Pokaż na mapie”."}
              </Alert>
            )}
          </Box>

          <Divider />
          <Box>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {t("products.details.pzWzAllocations") || "Zależności PZ–WZ"}
            </Typography>
            <PzWzAllocationsPanel
              receipts={allocations?.receipts}
              unallocatedOutgoing={allocations?.unallocated_outgoing}
              loading={allocationsLoading}
            />
          </Box>

          <Divider />
          <Box>
            <Table<ProductDocumentInfo>
              key={id}
              title={t("products.details.relatedDocuments") || "Powiązane dokumenty"}
              columns={relatedDocsColumns}
              data={relatedDocuments}
              page={docsPage}
              rowsPerPage={docsRowsPerPage}
              totalRows={totalDocsCount}
              onPageChange={setDocsPage}
              onRowsPerPageChange={setDocsRowsPerPage}
              rowKey="id"
              onRowClick={(row) => navigate(`/documents/${row.id}`)}
              loading={docsLoading}
              emptyMessage={t("products.details.noDocuments") || "Brak powiązanych dokumentów"}
              searchable
              searchPlaceholder={t("common.search") || "Szukaj..."}
              onSearchChange={(term) => {
                setDocsSearch(term);
                setDocsPage(0);
              }}
            />
          </Box>

          <>
            <Divider />
            <TextField
              fullWidth
              label={t("products.form.description") || "Opis"}
              value={product.description ?? ""}
              onChange={handleChange("description")}
              variant="outlined"
              multiline
              rows={4}
              sx={textFieldStyles}
              autoComplete="off"
            />
          </>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/products")}
              disabled={isSaving}
              sx={{
                minWidth: 120,
                "&:hover": { backgroundColor: "primary.dark" },
              }}
            >
              {t("common.cancel") || "Anuluj"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{
                minWidth: 120,
                "&:hover": { backgroundColor: "primary.dark" },
              }}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t("common.saveChanges") || "Zapisz zmiany"
              )}
            </Button>
          </Box>
        </Stack>
      </CustomBox>

      {/* Bundle Assembly Dialog */}
      <Dialog
        open={bundleAssembleOpen}
        onClose={() => setBundleAssembleOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#2a2a2a",
            color: "white",
            border: "1px solid #474747ff",
            borderRadius: "11px",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {t("bundles.assembleTitle") || "Montaż pakietu"}
          <IconButton onClick={() => setBundleAssembleOpen(false)} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            <Autocomplete
              options={bundleDefinitions}
              getOptionLabel={(o) => `${o.bundle_product_name} (${o.bundle_product_code})`}
              value={bundleAssembleDef}
              onChange={(_, val) => setBundleAssembleDef(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("bundles.selectDefinition") || "Wybierz definicję pakietu"}
                  sx={getFormTextFieldStyles()}
                />
              )}
              slotProps={{
                listbox: { sx: getAutocompleteListboxStyles() },
                paper: { sx: { backgroundColor: "#1e1e1e", border: "1px solid #474747ff", color: "white", backgroundImage: "none" } },
              }}
              isOptionEqualToValue={(o, v) => o.id === v.id}
            />

            {bundleAssembleDef && (
              <Alert severity="info" sx={{ bgcolor: "rgba(255,255,255,0.05)", "& .MuiAlert-message": { color: "white" } }}>
                <Typography variant="body2" color="white">
                  {t("bundles.componentsNeeded") || "Wymagane komponenty"}:
                </Typography>
                {bundleAssembleDef.components.map((c) => (
                  <Typography key={c.id} variant="body2" sx={{ ml: 1 }} color="white">
                    • {c.component_product_name}: {c.quantity_per_bundle * bundleAssembleQty} szt.
                  </Typography>
                ))}
              </Alert>
            )}

            <TextField
              label={t("bundles.quantity") || "Ilość pakietów"}
              type="number"
              value={bundleAssembleQty}
              onChange={(e) => setBundleAssembleQty(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
              sx={getFormTextFieldStyles()}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBundleAssembleOpen(false)} sx={{ color: "white" }}>
            {t("common.cancel") || "Anuluj"}
          </Button>
          <Button
            variant="contained"
            onClick={handleBundleAssemble}
            disabled={bundleAssembleSaving || !bundleAssembleDef}
            startIcon={<BuildIcon />}
          >
            {bundleAssembleSaving ? <CircularProgress size={24} /> : (t("bundles.assemble") || "Montuj")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductDetails;
